import React, { useEffect, useRef, useState } from 'react';
import { Link } from "react-router-dom";
import ArticleMarkdown from './ArticleMarkdown.js';

// One post on /words: title, and the start of the post rendered as normal,
// fading out. Clicking the faded part expands it into the full post.
function PostPreview({ post, name = "words", tags }) {
    const [expanded, setExpanded] = useState(false);
    const [overflowing, setOverflowing] = useState(true);
    const contentRef = useRef(null);
    const url = `/${name}/${post.url}`;

    // Only fade/offer expanding when the post is actually longer than the
    // collapsed height (re-checked as images load and change the height).
    useEffect(() => {
        const el = contentRef.current;
        if (!el || expanded) return;
        const check = () => setOverflowing(el.scrollHeight > el.clientHeight + 1);
        check();
        if (typeof ResizeObserver === "undefined") return;
        const observer = new ResizeObserver(check);
        Array.from(el.children).forEach(child => observer.observe(child));
        observer.observe(el);
        return () => observer.disconnect();
    }, [expanded, post.content]);

    const collapsed = !expanded && overflowing;

    const collapse = () => {
        setExpanded(false);
        // Bring the top of the post back into view after collapsing.
        const top = contentRef.current && contentRef.current.closest(".post-preview");
        if (top) top.scrollIntoView({ block: "nearest" });
    };

    return (
        <div className="post-preview">
            <div className="post-preview-body">
                <hgroup>
                    <h2><Link to={url}>{post.title}</Link></h2>
                    { post.subtitle && <subtitle>{post.subtitle}</subtitle>}
                    { post.publishTimeUnixSecs > 0 && <p>{new Date(post.publishTimeUnixSecs * 1000).toDateString()}</p> }
                </hgroup>
                <div className="post-preview-content-wrapper">
                    <div
                        ref={contentRef}
                        className={`post-preview-content ${name}${expanded ? " expanded" : ""}${collapsed ? " collapsed" : ""}`}
                    >
                        {/* Other embeds only load once expanded (except picture-swapped ones, see ArticleMarkdown). */}
                        <ArticleMarkdown content={post.content} embeds={expanded} />
                    </div>
                    { collapsed && (
                        <button
                            type="button"
                            className="post-preview-expand"
                            onClick={() => setExpanded(true)}
                            aria-label={`Show more of "${post.title}"`}
                        >
                            <span>more &#9662;</span>
                        </button>
                    ) }
                </div>
                { expanded && (
                    <p className="post-preview-collapse">
                        <button type="button" className="link-button" onClick={collapse}>collapse &#9652;</button>
                        {" · "}
                        <Link to={url}>permalink</Link>
                    </p>
                ) }
                { post["paraphernalia"] && tags }
            </div>
        </div>
    );
}

export default PostPreview;
